import {
  AppFeatureListWidget,
  FeatureAppsConfiguration,
  GetAppDataCallbackResult,
  IAppCardAppData,
} from '@orcha/micro-library-widgets-ui';
import { stringSlugifier } from '@orcha/utils';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import AppCard from 'src/components/AppCard/AppCard';
import DownloadDialog from 'src/components/DownloadDialog/DownloadDialog';

type AppFeatureListProps = {
  title: string;
  apps: Omit<
    FeatureAppsConfiguration,
    | 'handleDownloadClick'
    | 'handleViewAppDetailsClick'
    | 'transformedPlatformData'
  >[];
  styleOverrides: {
    backgroundColor: string;
    paddingTop: number;
    paddingBottom: number;
  };
  orderBy: 'none' | 'name' | 'score' | 'assured';
  cardAlignment: 'alternate' | 'left' | 'right';
  showAdditionalInfo: boolean;
  appData: IAppCardAppData[];
};

export const AppFeatureList = ({
  title,
  apps,
  styleOverrides,
  orderBy,
  cardAlignment,
  showAdditionalInfo,
  appData,
}: AppFeatureListProps) => {
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<IAppCardAppData | undefined>(
    undefined
  );
  const router = useRouter();
  const { t } = useTranslation('microsite');

  const appDataMap = useMemo(() => {
    return new Map(appData.map((app) => [app.appId, app]));
  }, [appData]);

  const transformedPlatformData = useCallback(
    (app: IAppCardAppData | undefined) => {
      if (!app?.platformData) return [];

      return app.platformData
        .filter((p) => p.platformName)
        .map((p) => {
          const { platformName, level, score, levelFurtherInfoUrl } = p;
          return {
            platformName,
            platformTitle: platformName
              ? t(`appCommon:platformLabels.${platformName}` as const)
              : '',
            score: score ? Math.round(score) : undefined,
            ...(level && {
              level: t('appCard:level.label', { level }),
            }),
            ...(levelFurtherInfoUrl && {
              levelFurtherInfo: t('appCard:level.explanation', {
                linkTarget: levelFurtherInfoUrl,
              }),
            }),
          };
        });
    },
    [t]
  );

  const getAppData = useCallback(
    (appId: string) => {
      const app = appDataMap.get(appId);
      if (!app) return undefined;

      return {
        ...app,
        exposeAppCardComponent: <AppCard appData={app} />,
      } as GetAppDataCallbackResult;
    },
    [appDataMap]
  );

  const renderDownloadDialog = useCallback(
    (app: IAppCardAppData | undefined) => {
      if (!app) return null;

      const platforms = app.platformData
        .filter((x) => x.platformName)
        .map((p) => ({
          platformName: p.platformName,
          downloadLink: p.downloadLink,
        }));

      return (
        <DownloadDialog
          app={{
            appId: app.appId,
            name: app.name,
            logoUrl: app.logoUrl,
            cost: app.cost,
            platforms,
            appStatus: app.appStatus,
          }}
          open={isDownloadDialogOpen}
          handleClose={() => {
            setIsDownloadDialogOpen(false);
            setSelectedApp(undefined);
          }}
        />
      );
    },
    [isDownloadDialogOpen]
  );

  const handleDownloadClick = useCallback(
    (app: IAppCardAppData | undefined) => {
      if (!app) return;
      setSelectedApp(app);
      setIsDownloadDialogOpen(true);
    },
    []
  );

  const buildAppDetailsUrl = useCallback((app: IAppCardAppData | undefined) => {
    if (!app) return '';
    const appNameSlugified = stringSlugifier(app.name);
    return `/app/${appNameSlugified}/${app.appId.toLowerCase()}`;
  }, []);

  const handleCardClick = useCallback(
    (app: IAppCardAppData | undefined) => {
      if (!app) return;
      const appDetailsUrl = buildAppDetailsUrl(app);
      router.push(appDetailsUrl);
    },
    [buildAppDetailsUrl, router]
  );

  const mappedApps = useMemo(() => {
    return apps.map((app) => {
      const appCardAppData = appDataMap.get(app.appId);
      return {
        appId: app.appId,
        bodyHtml: app.bodyHtml,
        backgroundImage: app.backgroundImage,
        transformedPlatformData: transformedPlatformData(appCardAppData),
        handleDownloadClick: () => handleDownloadClick(appCardAppData),
        handleViewAppDetailsClick: () => handleCardClick(appCardAppData),
      };
    });
  }, [
    apps,
    appDataMap,
    transformedPlatformData,
    handleDownloadClick,
    handleCardClick,
  ]);

  const translations = useMemo(
    () => ({
      appDetailsButton: t('appFeatureWidget.appDetailsButton'),
      downloadButton: t('appFeatureWidget.downloadButton'),
      platformAccreditationsHeading: t('appDetails:accreditationsHeading'),
      accreditationBadgeTooltipText: t('appDetails:accreditationAltText'),
    }),
    [t]
  );

  return (
    <>
      <AppFeatureListWidget
        title={title}
        styleOverrides={styleOverrides}
        orderBy={orderBy}
        cardAlignment={cardAlignment}
        apps={mappedApps}
        FlexibleImageComponent={Image}
        showAdditionalInfo={showAdditionalInfo}
        getAppDataCallback={getAppData}
        translations={translations}
      />
      {isDownloadDialogOpen && renderDownloadDialog(selectedApp)}
    </>
  );
};
